import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, Button, Spin, message, Alert } from "antd";
import {
  CheckCircleOutlined,
  SendOutlined,
  BulbOutlined,
  CopyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { getExpressApiBaseUrl } from "../lib/api/config";

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: var(--color-bg-primary, #f5f5f5);
`;

const StyledCard = styled(Card)`
  max-width: 480px;
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  .ant-card-body {
    padding: 1.5rem;
  }
`;

const IssueTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const IssueDescription = styled.div`
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 1.25rem;
  white-space: pre-wrap;
  word-break: break-word;
`;

const LeoBlock = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.25rem;
`;

const LeoLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-primary, #2c5f2d);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const DraftMessage = styled.div`
  font-size: 0.95rem;
  color: #166534;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const ApproveButton = styled(Button)`
  width: 100%;
  height: 48px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 12px;
`;

export default function QuickActionPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [approved, setApproved] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing link");
      setLoading(false);
      return;
    }
    const apiUrl = getExpressApiBaseUrl();
    fetch(`${apiUrl.replace(/\/$/, "")}/quick-action/${token}`)
      .then((res) => {
        if (!res.ok)
          throw new Error(
            res.status === 404 ? "Invalid or expired link" : "Failed to load"
          );
        return res.json();
      })
      .then((body) => {
        if (body.draftAction && body.draftAction.status !== "draft") {
          setError("This action was already completed.");
        } else {
          setData(body);
        }
      })
      .catch((err) => setError(err.message || "Something went wrong"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleApprove = async () => {
    if (!token || approved) return;
    setSending(true);
    try {
      const apiUrl = getExpressApiBaseUrl();
      const res = await fetch(
        `${apiUrl.replace(/\/$/, "")}/quick-action/${token}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || "Failed to send");
      setApproved(true);
      message.success("Vendor notified.");
    } catch (err) {
      message.error(err.message || "Failed to approve");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <StyledCard>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
            <div style={{ marginTop: "1rem", color: "#666" }}>Loading...</div>
          </div>
        </StyledCard>
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page>
        <StyledCard>
          <Alert
            type="error"
            message="Unable to load"
            description={error || "Invalid or expired link."}
            showIcon
          />
        </StyledCard>
      </Page>
    );
  }

  const draft = data.draftAction || {};
  const ticket = data.ticket || {};
  const issueSummary =
    draft.issueSummary || ticket.title || "Maintenance request";
  const description = ticket.description || "";
  const draftMessage = draft.draftVendorMessage || "";
  const vendorName = draft.vendorName || "Vendor";
  const nextSteps = Array.isArray(draft.nextSteps) ? draft.nextSteps : [];
  const canApprove =
    draft.status === "draft" &&
    draft.draftVendorMessage &&
    draft.vendorEmail &&
    !approved;
  const noVendorOrMessage =
    draft.status === "draft" &&
    (!draft.vendorEmail || !draft.draftVendorMessage);

  // Text to copy: AI diagnosis (issue + next steps + draft message if any)
  const copyableDraftText = [
    issueSummary,
    description ? `\n${description}` : "",
    nextSteps.length
      ? `\nNext steps:\n${nextSteps.map((s, i) => `${i + 1}. ${s}`).join("\n")}`
      : "",
    draftMessage ? `\nDraft message:\n${draftMessage}` : "",
  ]
    .filter(Boolean)
    .join("");

  const handleCopyDraft = async () => {
    if (!copyableDraftText) return;
    try {
      await navigator.clipboard.writeText(copyableDraftText);
      message.success("Copied to clipboard.");
    } catch {
      message.error("Could not copy.");
    }
  };

  return (
    <Page>
      <StyledCard>
        <IssueTitle>{issueSummary}</IssueTitle>
        {description && <IssueDescription>{description}</IssueDescription>}

        {draftMessage && (
          <LeoBlock>
            <LeoLabel>
              <BulbOutlined /> Clumse AI&apos;s drafted message
            </LeoLabel>
            <DraftMessage>{draftMessage}</DraftMessage>
            {vendorName && (
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#166534",
                  marginTop: "0.5rem",
                }}
              >
                Recommended: {vendorName}
              </div>
            )}
          </LeoBlock>
        )}

        {noVendorOrMessage && (
          <Alert
            type="warning"
            message="Manual action required"
            description="No preferred vendor or auto-send is set up for this request. Use the copy button below to paste Clumse AI's diagnosis into your own message or system."
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
        )}

        {copyableDraftText && (
          <Button
            type="default"
            size="middle"
            icon={<CopyOutlined />}
            onClick={handleCopyDraft}
            style={{ marginBottom: 16, width: "100%" }}
          >
            Copy draft
          </Button>
        )}

        {approved ? (
          <Alert
            type="success"
            message="Sent"
            description="The vendor has been notified."
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : canApprove ? (
          <ApproveButton
            type="primary"
            size="large"
            icon={<SendOutlined />}
            onClick={handleApprove}
            loading={sending}
          >
            Approve & Send
          </ApproveButton>
        ) : !noVendorOrMessage ? (
          <Alert
            type="info"
            message="No action needed"
            description="This draft was already sent or cannot be sent."
            showIcon
          />
        ) : null}
      </StyledCard>
    </Page>
  );
}
